import { Button, LogoIcon, Sidebar } from '@profitowi/component-library';
import clsx from 'clsx';
import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import useAgentRoleContext from 'hooks/useAgentRoleContext';
import useDocumentContext from 'hooks/useDocumentContext';
import { Roles } from 'types/user';

type Props = {
  className: string;
  icon: string;
  to: string;
  name: string;
  external: boolean;
  isDisabled?: boolean;
  customContent?: React.ReactNode;
};

const NavbarButton = ({
  className,
  icon,
  to,
  name,
  external,
  isDisabled,
  customContent,
}: Props) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const match = useMemo(() => {
    if (to === '/') {
      return pathname === to;
    }

    return to === '' ? false : pathname.includes(to);
  }, [pathname, to]);

  return (
    <Button
      className={className}
      isActive={match}
      onPress={() => {
        if (external) {
          window.open(to, '_blank');
        } else {
          navigate(to);
        }
      }}
      isDisabled={isDisabled}
      variant="light">
      <i className={`bi ${icon}`} />
      <span>{name}</span>
      {customContent}
    </Button>
  );
};

const Navbar = () => {
  const { hasRole, isAdminOrCommissionRole, shouldDisplayWarningBanner } = useAgentRoleContext();

  const { documentCount } = useDocumentContext();

  const UnreadDocumentsCounter = useMemo(() => {
    if (!documentCount) return <></>;
    return (
      <div className="bg-rose-600 !ml-auto text-white font-bold px-2 w-min">{documentCount}</div>
    );
  }, [documentCount]);

  return (
    <Sidebar className="!h-[98vh]">
      <div className={clsx('space-y-6', { 'pt-12': shouldDisplayWarningBanner })}>
        <Link className="flex justify-center" to="/">
          <LogoIcon />
        </Link>

        <nav>
          <NavbarButton
            className="w-full text-left space-x-2 outline-none"
            icon="bi-grid"
            key="Dashboard"
            name="Dashboard"
            to="/"
            external={false}
            isDisabled={!isAdminOrCommissionRole}
          />

          <NavbarButton
            className="w-full text-left space-x-2 outline-none"
            icon="bi-wallet"
            key="Ewidencje"
            name="Ewidencje"
            to="/records"
            external={false}
            isDisabled={!isAdminOrCommissionRole && !hasRole(Roles.RECORDS_MANAGEMENT)}
          />

          <NavbarButton
            className="w-full text-left space-x-2 outline-none"
            icon="bi-bar-chart"
            key="Prowizje"
            name="Prowizje"
            to="/commissions"
            external={false}
            isDisabled={!isAdminOrCommissionRole}
          />
          <NavbarButton
            className="w-full text-left space-x-2 outline-none"
            icon="bi-stickies"
            key="Rozliczenia"
            name="Rozliczenia"
            to="/settlements"
            external={false}
            isDisabled={!isAdminOrCommissionRole}
          />
          <NavbarButton
            className="w-full flex text-left space-x-2 outline-none"
            icon="bi-file-text"
            key="Dokumenty"
            name="Dokumenty"
            to="/documents"
            external={false}
            customContent={UnreadDocumentsCounter}
          />
          {/* // Hide communication tab for now @PROFMP140-3 */}
          {/* <NavbarButton
            className="w-full text-left space-x-2 outline-none"
            icon="bi-chat-left-text"
            key="Wiadomości"
            name="Wiadomości"
            to="/communication"
            external={false}
            isDisabled={!isAdminOrCommissionRole && !hasRole(Roles.RECORDS_MANAGEMENT)}
          /> */}

          <NavbarButton
            className="w-full text-left space-x-2 outline-none"
            icon="bi-collection"
            key="Raporty"
            name="Raporty"
            to="/reports"
            external={false}
            isDisabled={!hasRole(Roles.REPORTS) && !isAdminOrCommissionRole}
          />
        </nav>
      </div>

      <nav>
        <NavbarButton
          className="w-full text-left space-x-2 outline-none"
          icon="bi-bell"
          key="Ustawienia"
          name="Ustawienia"
          to="/ustawienia"
          external={false}
          isDisabled={
            !isAdminOrCommissionRole &&
            !hasRole(Roles.RECORDS_MANAGEMENT) &&
            !hasRole(Roles.DOCUMENTS_MANAGEMENT_TEMPLATE)
          }
        />
        <NavbarButton
          className="w-full text-left space-x-2 outline-none"
          icon="bi-person"
          key="Moje konto"
          name="Moje konto"
          to="/konto"
          external={false}
        />
        <NavbarButton
          className="w-full text-left space-x-2 outline-none"
          icon="bi-box-arrow-left"
          key="Wyloguj"
          name="Wyloguj"
          to="/wyloguj"
          external={false}
        />
      </nav>
    </Sidebar>
  );
};

export default Navbar;
